import { memo } from "react";
import { Handle, Position, NodeProps, Node } from "@xyflow/react";
import { User, Users, Monitor, Globe, FolderTree, Box, AppWindow, Shield } from "lucide-react";

export type ADNodeSizeTier = 'tiny' | 'small' | 'medium' | 'large' | 'huge';

export interface ADNodeData extends Record<string, unknown> {
  label: string;
  kind: string;
  isTierZero: boolean;
  isOwnedObject: boolean;
  nodeId: string;
  /** @deprecated Use sizeTier instead. Kept for backwards compatibility. */
  compact?: boolean;
  graphSize?: number;
  /** Visual tier picked by ADReactFlowGraph based on graph size. */
  sizeTier?: ADNodeSizeTier;
  /** Whether to render the label under the node. */
  showLabel?: boolean;
}

export type ADFlowNode = Node<ADNodeData>;

const kindColors: Record<string, string> = {
  User: "#38bdf8",
  Group: "#10b981",
  Computer: "#f59e0b",
  Domain: "#ef4444",
  OU: "#06b6d4",
  Container: "#8b5cf6",
  App: "#f97316",
  GPO: "#ec4899",
  Default: "#94a3b8",
};

interface TierStyle {
  size: number;
  iconClass: string;
  labelClass: string;
  tierZeroDot: number;
  borderWidth: number;
}

const TIER_STYLE: Record<ADNodeSizeTier, TierStyle> = {
  // Compact rendering for huge graphs (>500 nodes)
  tiny: {
    size: 80,
    iconClass: 'w-10 h-10',
    labelClass: 'mt-2 text-base font-medium max-w-[180px]',
    tierZeroDot: 14,
    borderWidth: 2,
  },
  small: {
    size: 120,
    iconClass: 'w-14 h-14',
    labelClass: 'mt-2 text-lg font-medium max-w-[220px]',
    tierZeroDot: 18,
    borderWidth: 2,
  },
  medium: {
    size: 160,
    iconClass: 'w-16 h-16',
    labelClass: 'mt-3 text-xl font-medium max-w-[280px]',
    tierZeroDot: 22,
    borderWidth: 2,
  },
  large: {
    size: 200,
    iconClass: 'w-20 h-20',
    labelClass: 'mt-3 text-2xl font-medium max-w-[340px]',
    tierZeroDot: 26,
    borderWidth: 2,
  },
  huge: {
    size: 240,
    iconClass: 'w-24 h-24',
    labelClass: 'mt-4 text-3xl font-semibold max-w-[420px]',
    tierZeroDot: 30,
    borderWidth: 2,
  },
};

function resolveTier(data: ADNodeData): ADNodeSizeTier {
  if (data.sizeTier) return data.sizeTier;
  // Backwards-compatible fallback based on graphSize.
  const size = data.graphSize ?? 0;
  if (size > 500) return 'tiny';
  if (size > 200) return 'small';
  if (size > 80) return 'medium';
  if (size > 30) return 'large';
  return 'huge';
}

function KindIcon({ kind, className }: { kind: string; className: string }) {
  switch (kind) {
    case "User":
      return <User className={className} />;
    case "Group":
      return <Users className={className} />;
    case "Computer":
      return <Monitor className={className} />;
    case "Domain":
      return <Globe className={className} />;
    case "OU":
      return <FolderTree className={className} />;
    case "Container":
      return <Box className={className} />;
    case "App":
      return <AppWindow className={className} />;
    case "GPO":
      return <Shield className={className} />;
    default:
      return <Globe className={className} />;
  }
}

function ADNodeComponent({ data, selected }: NodeProps<ADFlowNode>) {
  const nodeData = data as ADNodeData;
  const color = kindColors[nodeData.kind] || kindColors.Default;
  const tier = resolveTier(nodeData);
  const style = TIER_STYLE[tier];
  const showLabel = nodeData.showLabel ?? true;

  return (
    <div className="flex flex-col items-center cursor-pointer select-none">
      <div className="relative">
        {nodeData.isTierZero && (
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: style.size + 12,
              height: style.size + 12,
              top: -6,
              left: -6,
              background: `radial-gradient(circle, ${color}33 0%, transparent 72%)`,
            }}
          />
        )}

        <div
          className={`relative rounded-full flex items-center justify-center ${
            selected ? "ring-2 ring-primary/70 shadow-[0_0_10px_rgba(59,130,246,0.25)]" : ""
          }`}
          style={{
            width: style.size,
            height: style.size,
            border: `${style.borderWidth}px solid ${color}`,
            backgroundColor: "hsl(var(--card))",
            color,
          }}
        >
          <Handle
            type="target"
            position={Position.Left}
            className="!opacity-0 !w-0 !h-0 !border-0"
            style={{ left: "50%", top: "50%" }}
          />
          <Handle
            type="source"
            position={Position.Right}
            className="!opacity-0 !w-0 !h-0 !border-0"
            style={{ right: "50%", top: "50%" }}
          />

          <KindIcon kind={nodeData.kind} className={style.iconClass} />

          {nodeData.isTierZero && (
            <div
              className="absolute rounded-full border-2 border-background"
              style={{
                width: style.tierZeroDot,
                height: style.tierZeroDot,
                backgroundColor: "#ef4444",
                top: style.size * 0.08,
                right: style.size * 0.08,
              }}
              title="Tier Zero"
            />
          )}
        </div>
      </div>

      {showLabel && (
        <span className={`${style.labelClass} text-foreground text-center truncate`}>
          {nodeData.label}
        </span>
      )}
    </div>
  );
}

export const ADNode = memo(
  ADNodeComponent,
  (prev, next) =>
    prev.selected === next.selected &&
    prev.dragging === next.dragging &&
    prev.data === next.data,
);

ADNode.displayName = "ADNode";
